/**
 * Low-sanity voices. Nothing is ever there.
 *
 * Gated entirely on sanity: above roughly half the player never hears one, and
 * below that they arrive at a rate that quietly doubles as the bar empties.
 * They are placed behind the player more often than not, because the whole
 * point is the half second spent deciding whether to turn around.
 */

import { TAU } from '../core/math';
import { smoothstep } from '../core/math';
import type { Vec2 } from '../core/types';
import type { AudioGraph } from './graph';
import type { Sfx } from './sfx';

export class Whispers {
  private timer = 12;
  private readonly at: Vec2 = { x: 0, z: 0 };

  constructor(
    private readonly g: AudioGraph,
    private readonly sfx: Sfx,
  ) {}

  /** `sanity` is 0..1. */
  update(dt: number, sanity: number, enabled: boolean): void {
    const pressure = 1 - smoothstep(0.12, 0.5, sanity);
    if (!enabled || pressure <= 0.02) {
      if (this.timer < 6) this.timer = this.g.rng.range(8, 20);
      return;
    }

    this.timer -= dt * (0.35 + pressure * 2.2);
    if (this.timer > 0) return;
    this.timer = this.g.rng.range(12, 38);

    const r = this.g.rng;
    const l = this.g.listener;
    // Forward is the right vector turned a quarter turn, so behind is its negation.
    const behind = Math.atan2(l.rx, -l.rz);
    const a = r.chance(0.65) ? behind + r.range(-1.2, 1.2) : r.range(0, TAU);
    const d = r.range(3, 13);
    this.at.x = l.x + Math.cos(a) * d;
    this.at.z = l.z + Math.sin(a) * d;

    this.sfx.play(r.chance(0.24) ? 'distant-voice' : 'whisper', {
      pos: this.at,
      gain: 0.5 + pressure * 0.6,
      rate: r.range(0.9, 1.12),
    });
  }
}
